import { useState, useEffect } from 'react';
import { ChevronDown, ChevronRight, CheckCircle, Clock, GraduationCap, Eye } from 'lucide-react';
import { Button } from '../ui/button';
import { PlanNoStudentsEmptyState } from './LessonsPage/components/PlanNoStudentsEmptyState';
import {
    lessonsUnitElementId,
    scheduleScrollUnitCardIntoView
} from './LessonsPage/utils/lessonUnitNavigation';
import { Card } from '../ui/card';
import { cn } from '../lib/utils';
import { formatTimeDisplay, formatIsoDateTimeShort } from '../utils/timeUtils';
import { AssessmentCard } from './InProgressUnitsPanel/AssessmentCard';
import { UnitSectionHeading } from './InProgressUnitsPanel/UnitSectionHeading';
import { UnitDto, InstructionStepDto } from './InProgressUnitsPanel/types';
import { LessonSupportingMaterialsSection } from './InProgressUnitsPanel/LessonSupportingMaterialsSection';
import { isLessonStarted } from './InProgressUnitsPanel/lessonStepState';
import { resolveLessonMaterials } from './InProgressUnitsPanel/instructionStepUtils';
import { UnitCardHeaderObjectivesProgress } from './InProgressUnitsPanel/UnitCardHeaderObjectivesProgress';
import {
    UnitHeaderSubjectLine,
    type UnitHeaderTone,
} from './UnitHeaderTopicLabels';
import { UnitHeaderTopicsWithObjectives } from './UnitHeaderTopicsWithObjectives';
import { UnitCardCreatedByMeta } from './UnitCardCreatedByMeta';
import { deriveUnitHeaderFields, formatUnitShortCalendarDate } from './unitHeaderDisplay';
import {
    unitCardBodyClass,
    unitCardBodyMetaClass,
    unitCardExpandableTitleClass,
    unitCardHeaderClass,
    unitCardShellClass,
} from './unitCardTheme';
import { LessonStepProgressList } from './InProgressUnitsPanel/LessonStepProgressList';
import { formatHowDidLessonGoDisplay } from './InProgressUnitsPanel/lessonFeedbackUtils';

interface LessonDetailDto {
    e251_LessonId?: string;
    e251_LessonTitle?: string;
    e251_LessonDuration?: number;
    e251_LessonSummary?: string;
    e251_LessonMaterials?: string;
    e251_LessonState?: string;
    e251_CompletionDate?: string | null;
    e251_HowDidLessonGo?: string;
    e251_WhatCouldBeImproved?: string;
    e251_InstructionSteps?: InstructionStepDto[];
}

interface CompletedUnitsPanelProps {
    units: UnitDto[];
    viewState: number;
    viewStates: { loading: number; ready: number };
    students: Array<{ id: string; name: string }>;
    isStudentLoading: boolean;
    passFailThreshold?: number | null;
    lessonUnitFocus?: { unitId: string; token: number } | null;
    onConsumedLessonUnitFocus?: () => void;
    onRefresh?: () => void;
}

export function CompletedUnitsPanel({
    units,
    viewState,
    viewStates,
    students,
    isStudentLoading,
    passFailThreshold = null,
    lessonUnitFocus = null,
    onConsumedLessonUnitFocus,
    onRefresh
}: CompletedUnitsPanelProps) {
    const [expandedUnits, setExpandedUnits] = useState<Set<string>>(new Set());
    const [expandedLessons, setExpandedLessons] = useState<Set<string>>(new Set());
    const [expandedAssessments, setExpandedAssessments] = useState<Set<string>>(new Set());

    const toggleUnit = (unitId: string) => {
        const wasExpanded = expandedUnits.has(unitId);
        if (wasExpanded) {
            setExpandedUnits(new Set());
            return;
        }
        setExpandedUnits(new Set([unitId]));
        scheduleScrollUnitCardIntoView(unitId);
    };

    const toggleLesson = (lessonId: string) => {
        setExpandedLessons(prev => {
            const newSet = new Set(prev);
            if (newSet.has(lessonId)) {
                newSet.delete(lessonId);
            } else {
                newSet.add(lessonId);
            }
            return newSet;
        });
    };

    const toggleAssessment = (assessmentId: string) => {
        setExpandedAssessments(prev => {
            const newSet = new Set(prev);
            if (newSet.has(assessmentId)) {
                newSet.delete(assessmentId);
            } else {
                newSet.add(assessmentId);
            }
            return newSet;
        });
    };

    useEffect(() => {
        if (!lessonUnitFocus?.unitId || !onConsumedLessonUnitFocus) return;

        const unitId = lessonUnitFocus.unitId;
        if (!units.some(u => u.e251_UnitId === unitId)) return;

        setExpandedUnits(new Set([unitId]));

        const handle = scheduleScrollUnitCardIntoView(unitId, { delayMs: 0 });
        const consumeHandle = window.setTimeout(() => {
            onConsumedLessonUnitFocus();
        }, 0);
        return () => {
            window.clearTimeout(handle);
            window.clearTimeout(consumeHandle);
        };
    }, [lessonUnitFocus, units, onConsumedLessonUnitFocus]);

    switch (viewState) {
        case viewStates.loading:
            // Show "No Students" message if there are no students and not loading students
            if (!isStudentLoading && students.length === 0) {
                return (
                    <PlanNoStudentsEmptyState description="You need to create a student before you can view completed units." />
                );
            }
            return (
                <div className="flex items-center justify-center py-12">
                    <div className="flex flex-col items-center gap-3">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-action-blue"></div>
                        <span className="text-gray-600">Loading units...</span>
                    </div>
                </div>
            );

        case viewStates.ready:
            if (units.length === 0) {
                return (
                    <div className="text-center py-12">
                        <CheckCircle className="mx-auto h-12 w-12 text-gray-400" />
                        <h3 className="mt-2 text-sm font-medium text-gray-900">No completed units</h3>
                        <p className="mt-1 text-sm text-gray-500">Complete units from the In Progress tab</p>
                    </div>
                );
            }

            return (
        <div className="space-y-4">
            {[...units].sort((a, b) => {
                // Sort units by completion date, most recent first
                const dateA = a.e251_CompletionDate ? new Date(a.e251_CompletionDate).getTime() : 0;
                const dateB = b.e251_CompletionDate ? new Date(b.e251_CompletionDate).getTime() : 0;
                return dateB - dateA; // Descending order (most recent first)
            }).map((unit) => {
                const unitId = unit.e251_UnitId || '';
                const isUnitExpanded = expandedUnits.has(unitId);
                const lessons = unit.e251_Lessons || [];
                const { topics, subjects, learningStyles, totalDuration, createdAt, createdByName } =
                    deriveUnitHeaderFields(unit);
                const completionDate = formatUnitShortCalendarDate(unit.e251_CompletionDate ?? null);
                const isPeerUnitDimmed = expandedUnits.size > 0 && !isUnitExpanded;
                const unitHeaderTone: UnitHeaderTone = 'default';
                const reviewHeaderButton = !isUnitExpanded ? (
                    <Button
                        variant="default"
                        size="sm"
                        onClick={() => toggleUnit(unitId)}
                        className="bg-action-blue hover:bg-action-blue-dark text-white"
                    >
                        <Eye size={16} className="mr-2" />
                        Review
                    </Button>
                ) : null;
                const completionStatus = completionDate ? (
                    <div className="flex shrink-0 items-center gap-1.5">
                        <span
                            className={cn(
                                'whitespace-nowrap text-sm font-medium',
                                isUnitExpanded ? 'text-confirm-action/80' : 'text-confirm-action'
                            )}
                        >
                            Completed: {completionDate}
                        </span>
                        <CheckCircle
                            size={18}
                            className={cn(
                                'shrink-0',
                                isUnitExpanded ? 'text-confirm-action/80' : 'text-confirm-action'
                            )}
                        />
                    </div>
                ) : null;

                return (
                    <Card
                        key={unitId}
                        id={lessonsUnitElementId(unitId)}
                        className={cn(
                            unitCardShellClass,
                            'transition-[opacity,filter] duration-200 ease-out',
                            isPeerUnitDimmed && 'opacity-[0.88] contrast-[0.97] saturate-[0.96]'
                        )}
                    >
                        {/* Unit Header */}
                        <div
                            onClick={() => toggleUnit(unitId)}
                            className={unitCardHeaderClass}
                        >
                            {reviewHeaderButton ? (
                                <div className="md:hidden mb-3" onClick={(e) => e.stopPropagation()}>
                                    <div className="flex flex-col gap-2">
                                        {reviewHeaderButton}
                                    </div>
                                </div>
                            ) : null}
                            <div className="flex flex-col gap-3">
                                <div className="flex items-center justify-between gap-4">
                                    <div className="flex min-w-0 flex-1 items-center">
                                        {isUnitExpanded ? (
                                            <ChevronDown size={20} className="mr-3 shrink-0 self-center text-gray-600" />
                                        ) : (
                                            <ChevronRight size={20} className="mr-3 shrink-0 self-center text-gray-600" />
                                        )}
                                        <div className="min-w-0 flex-1 flex flex-col gap-3">
                                            <div className="flex flex-col gap-1.5 md:hidden">
                                                <h3 className="m-0 text-lg font-normal text-gray-900">
                                                    <UnitHeaderSubjectLine
                                                        subjects={subjects}
                                                        tone={unitHeaderTone}
                                                    />
                                                </h3>
                                                {completionStatus}
                                            </div>
                                            <h3 className="m-0 hidden text-lg font-normal text-gray-900 md:block">
                                                <div className="flex items-center justify-between gap-4">
                                                    <UnitHeaderSubjectLine
                                                        subjects={subjects}
                                                        tone={unitHeaderTone}
                                                    />
                                                    {completionStatus}
                                                </div>
                                            </h3>
                                            <div className="md:hidden">
                                                <UnitHeaderTopicsWithObjectives
                                                    topics={topics}
                                                    learningObjectives={unit.e251_LearningObjectives}
                                                    showStatus
                                                    tone={unitHeaderTone}
                                                />
                                                <UnitCardHeaderObjectivesProgress
                                                    learningObjectives={unit.e251_LearningObjectives}
                                                />
                                            </div>
                                        </div>
                                    </div>
                                    <div
                                        className="hidden shrink-0 self-center md:flex md:flex-col lg:flex-row md:items-stretch lg:items-center gap-2 ml-4"
                                        onClick={(e) => e.stopPropagation()}
                                    >
                                        {reviewHeaderButton}
                                    </div>
                                </div>
                                <div className="hidden min-w-0 w-full flex-col gap-3 pl-8 md:flex">
                                    <UnitHeaderTopicsWithObjectives
                                        topics={topics}
                                        learningObjectives={unit.e251_LearningObjectives}
                                        showStatus
                                        tone={unitHeaderTone}
                                    />
                                    <UnitCardHeaderObjectivesProgress
                                        learningObjectives={unit.e251_LearningObjectives}
                                    />
                                </div>
                            </div>
                        </div>

                        {/* Unit Content */}
                        {isUnitExpanded && (
                            <div className={unitCardBodyClass}>
                                <div className="p-6">
                                    {(learningStyles.length > 0 || totalDuration > 0 || createdAt || createdByName) ? (
                                        <div className={unitCardBodyMetaClass}>
                                            {totalDuration > 0 && (
                                                <span className="flex items-center">
                                                    <Clock size={14} className="mr-1 text-gray-600" />
                                                    {formatTimeDisplay(totalDuration)}
                                                </span>
                                            )}
                                            {learningStyles.length > 0 && (
                                                <span className="flex items-center">
                                                    <GraduationCap size={14} className="mr-1 text-gray-600" />
                                                    {learningStyles.join(', ')}
                                                </span>
                                            )}
                                            <UnitCardCreatedByMeta
                                                createdAt={createdAt}
                                                createdByName={createdByName}
                                                tone="default"
                                            />
                                        </div>
                                    ) : null}

                                    {/* Assessment Card */}
                                    {unit.e251_AssessmentId && (
                                        <AssessmentCard
                                            unit={unit}
                                            status={unit.e251_AssessmentStatus}
                                            isExpanded={expandedAssessments.has(unit.e251_AssessmentId || '')}
                                            onToggle={toggleAssessment}
                                            onRefresh={onRefresh}
                                            passFailThreshold={passFailThreshold}
                                        />
                                    )}

                                    {/* Lessons — always visible under a static heading */}
                                    <div>
                                        <UnitSectionHeading title={`Lessons (${lessons.length})`} />
                                        <div className="space-y-3">
                                        {lessons.map((lesson) => {
                                            const lessonId = lesson.e251_LessonId || '';
                                            const isLessonExpanded = expandedLessons.has(lessonId);
                                            const lessonDuration = lesson.e251_LessonDuration || 0;
                                            const instructionSteps = lesson.e251_InstructionSteps || [];
                                            const lessonCompletedAt = formatIsoDateTimeShort(lesson.e251_LessonCompletionDate ?? null);

                                            return (
                                                <Card key={lessonId} className="border border-gray-300 bg-white">
                                                    {/* Lesson Header */}
                                                    <div
                                                        onClick={() => toggleLesson(lessonId)}
                                                        className="w-full cursor-pointer px-4 py-3 text-left transition-colors hover:bg-gray-50"
                                                    >
                                                        <div className="flex items-center justify-between">
                                                            <div className="flex items-center flex-1">
                                                                {isLessonExpanded ? (
                                                                    <ChevronDown size={16} className="text-gray-500 mr-2" />
                                                                ) : (
                                                                    <ChevronRight size={16} className="text-gray-500 mr-2" />
                                                                )}
                                                                <div className="flex-1">
                                                                    <div className="flex items-center gap-2">
                                                                        <h5 className={cn('text-base font-medium text-gray-900', unitCardExpandableTitleClass)}>
                                                                            {lesson.e251_LessonTitle || 'Untitled Lesson'}
                                                                        </h5>
                                                                        <CheckCircle size={14} className="text-confirm-action" />
                                                                    </div>
                                                                    <div className="flex items-center gap-2 mt-1 text-sm text-gray-600 flex-wrap">
                                                                        <Clock size={12} className="mr-1" />
                                                                        {formatTimeDisplay(lessonDuration)}
                                                                        {instructionSteps.length > 0 && (
                                                                            <span>• {instructionSteps.length} steps</span>
                                                                        )}
                                                                        {lessonCompletedAt && (
                                                                            <span className="text-gray-500">
                                                                                • Completed {lessonCompletedAt}
                                                                            </span>
                                                                        )}
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>

                                                    {((lesson.e251_HowDidLessonGo && lesson.e251_HowDidLessonGo.trim()) ||
                                                      (lesson.e251_WhatCouldBeImproved &&
                                                          lesson.e251_WhatCouldBeImproved.trim())) && (
                                                        <div className="px-4 pb-3 border-t border-gray-100">
                                                            <h6 className="text-xs font-semibold text-gray-700 uppercase tracking-wide mb-2 mt-3">
                                                                Lesson Feedback
                                                            </h6>
                                                            {lesson.e251_HowDidLessonGo && lesson.e251_HowDidLessonGo.trim() && (
                                                                <div className="mb-2">
                                                                    <p className="text-xs font-medium text-gray-600 mb-1">
                                                                        How did the lesson go?
                                                                    </p>
                                                                    <p className="text-sm text-gray-700">
                                                                        {formatHowDidLessonGoDisplay(lesson.e251_HowDidLessonGo)}
                                                                    </p>
                                                                </div>
                                                            )}
                                                            {lesson.e251_WhatCouldBeImproved &&
                                                                lesson.e251_WhatCouldBeImproved.trim() && (
                                                                    <div>
                                                                        <p className="text-xs font-medium text-gray-600 mb-1">
                                                                            What could be improved?
                                                                        </p>
                                                                        <p className="text-sm text-gray-700">
                                                                            {lesson.e251_WhatCouldBeImproved}
                                                                        </p>
                                                                    </div>
                                                                )}
                                                        </div>
                                                    )}

                                                    {isLessonExpanded && (
                                                        <div className="px-4 pb-4 border-t border-gray-100">
                                                            <div className="pt-4 space-y-4">
                                                                {lesson.e251_LessonSummary && (
                                                                    <div>
                                                                        <h6 className="text-xs font-semibold text-gray-700 uppercase tracking-wide mb-1">
                                                                            Summary
                                                                        </h6>
                                                                        <p className="text-sm text-gray-600 whitespace-pre-wrap">
                                                                            {lesson.e251_LessonSummary}
                                                                        </p>
                                                                    </div>
                                                                )}
                                                                {lesson.e251_InstructionSteps && lesson.e251_InstructionSteps.length > 0 && (
                                                                    <div>
                                                                        <h6 className="text-xs font-semibold text-gray-700 uppercase tracking-wide mb-1">
                                                                            Instructions
                                                                        </h6>
                                                                        <ol className="list-decimal space-y-1 pl-5 text-sm text-gray-600">
                                                                            {lesson.e251_InstructionSteps.map((step, index) => (
                                                                                <li key={step.e251_StepId ?? `step-${index}`}>
                                                                                    {step.e251_Summary?.trim() || `Step ${step.e251_StepNumber ?? index + 1}`}
                                                                                </li>
                                                                            ))}
                                                                        </ol>
                                                                    </div>
                                                                )}
                                                                {instructionSteps.length > 0 && (
                                                                    <div>
                                                                        <h6 className="text-xs font-semibold text-gray-700 uppercase tracking-wide mb-2">
                                                                            Steps ({instructionSteps.length})
                                                                        </h6>
                                                                        <LessonStepProgressList steps={instructionSteps} />
                                                                    </div>
                                                                )}
                                                                <LessonSupportingMaterialsSection
                                                                    lessonStarted={isLessonStarted(lesson)}
                                                                    materialsToProvide={lesson.e251_LessonMaterials}
                                                                    materials={resolveLessonMaterials(lesson)}
                                                                    lessonTitle={lesson.e251_LessonTitle}
                                                                    instructionSteps={lesson.e251_InstructionSteps}
                                                                />
                                                            </div>
                                                        </div>
                                                    )}
                                                </Card>
                                            );
                                        })}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}
                    </Card>
                );
            })}
        </div>
    );
    }
}

